import { useCallback, useEffect, useRef, useState } from 'react'

import { useInfiniteScroll } from 'shared/hooks/use-infinity-scroll'
import { getApiErrorMessage } from 'shared/lib/errorText'

import type { ObjectApplication } from '../model/types'

import { fetchObjectApplicationsPage } from './fetch-object-applications-page'

const OBJECT_APPLICATIONS_PAGE_SIZE = 25

interface UseObjectApplicationsParams {
  orderRequestsCount?: number
}

export const useObjectApplications = ({
  orderRequestsCount,
}: UseObjectApplicationsParams = {}) => {
  const [applications, setApplications] = useState<ObjectApplication[]>([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const paginationTargetRef = useRef<HTMLDivElement>(null)

  const hasApplications = applications.length > 0
  const hasMore = page < totalPages
  const shouldSkipInitialLoader = orderRequestsCount === 0
  const shouldShowInitialLoader =
    !shouldSkipInitialLoader && (!hasLoaded || isLoading) && !hasApplications
  const shouldShowPaginationLoader = isLoading && hasApplications
  const shouldHideSection =
    (shouldSkipInitialLoader && !hasApplications) ||
    (hasLoaded && !isLoading && (Boolean(error) || !hasApplications))

  const fetchApplications = useCallback(async (nextPage: number) => {
    const isFirstPage = nextPage === 1

    if (isFirstPage) {
      abortControllerRef.current?.abort()
      setApplications([])
      setPage(1)
      setTotalPages(1)
      setHasLoaded(false)
    }

    const abortController = new AbortController()
    abortControllerRef.current = abortController

    setIsLoading(true)
    setError(null)

    try {
      const result = await fetchObjectApplicationsPage({
        page: nextPage,
        pageSize: OBJECT_APPLICATIONS_PAGE_SIZE,
        signal: abortController.signal,
      })

      setApplications((currentApplications) =>
        isFirstPage ? result.items : [...currentApplications, ...result.items],
      )
      setPage(result.page)
      setTotalPages(result.totalPages)
    } catch (requestError) {
      const errorMessage = getApiErrorMessage(requestError)

      if (errorMessage === null || abortController.signal.aborted) {
        return
      }

      setError(errorMessage)

      if (isFirstPage) {
        setApplications([])
      }
    } finally {
      if (!abortController.signal.aborted) {
        setIsLoading(false)
        setHasLoaded(true)
      }
    }
  }, [])

  useEffect(() => {
    void fetchApplications(1)

    return () => {
      abortControllerRef.current?.abort()
    }
  }, [fetchApplications])

  useInfiniteScroll({
    targetRef: paginationTargetRef,
    canLoad: hasMore && !isLoading && !error,
    onLoad: () => fetchApplications(page + 1),
    trigger: hasApplications,
  })

  const reloadApplications = useCallback(() => {
    return fetchApplications(1)
  }, [fetchApplications])

  return {
    applications,
    paginationTargetRef,
    reloadApplications,
    shouldHideSection,
    shouldShowInitialLoader,
    shouldShowPaginationLoader,
  }
}
